import { createContext, useEffect, useState } from "react";

import type { JSX, ReactNode } from "react";

export type ToastProps = {
  caption: string;
  explanation?: string;
  message: string;
};

type ToastPropsInternal = ToastProps & {
  setToast: React.Dispatch<React.SetStateAction<ToastProps | null>>;
};

type ToastContextType = {
  showToast: (props: ToastProps) => void;
};

export const Toast: React.FC<ToastPropsInternal> = ({
  message,
  explanation,
  caption,
  setToast,
}: ToastPropsInternal) => {
  return (
    <div className="toast-container bottom-0 end-0 mb-1 me-1">
      <div
        className="toast show"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div className="toast-header">
          <i className="bi bi-chat-right me-2"></i>
          <strong className="me-auto">{caption}</strong>
          <small>{explanation}</small>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="toast"
            aria-label="Close"
            onClick={() => setToast(null)}
          ></button>
        </div>
        <div className="toast-body">{message}</div>
      </div>
    </div>
  );
};

export const ToastContext = createContext<ToastContextType | undefined>(
  undefined,
);

export const ToastProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [toast, setToast] = useState<ToastProps | null>(null);

  const showToast = (props: ToastProps): void => {
    setToast(props);
  };

  useEffect(() => {
    if (toast === null) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setToast(null);
    }, 5000);

    return (): void => clearTimeout(timeoutId);
  }, [toast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && <Toast setToast={setToast} {...toast} />}
    </ToastContext.Provider>
  );
};
